import {Box, IconButton, InputAdornment, Popover} from '@mui/material';
import {GridColDef, GridFilterItem, GridFilterModel} from '@mui/x-data-grid';
import {GridApi} from '@mui/x-data-grid-pro';
import {t} from 'i18next';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {isEmpty} from '../../../../utils/utils';
import AurButton from '../../../common/aur-button/AurButton';
import AurDataGrid from '../../../common/aur-data-grid/AurDataGrid';
import AurIcon, {IconNames} from '../../../common/aur-icon/AurIcon';
import {AurTextField} from '../../../common/aur-text-field/AurTextField';
import FieldPopoverDateFilter, {
  Condition,
  SelectedDateType,
} from './FieldPopoverDateFilter';
import {ReactComponent as ClearIcon} from '../../../../assests/svg/clear-Icon-filled.svg';
import {EvidenceStatuses} from '../../../../constants/constants';

export type FieldGridFilterType = 'text' | 'options' | 'date';

export interface FieldFilterGridData {
  id: number | string;
  label: string;
  value?: string;
}

export interface FieldPopoverGridFilterProps {
  apiRef: React.MutableRefObject<GridApi>;
  type: FieldGridFilterType;
  dataArray?: FieldFilterGridData[];
  defaultArray?: (number | string)[];
  valueGetterObj?: object;
  columnField: string;
  hideFooter?: boolean;
  inputClass?: string;
  inputType?: 'text' | 'search';
  caretIconContainerClass?: string;
  excludeDateFilterConditions?: string[];
  /**
   * Set this true to get Not Set option selection in grid filter apply callback and
   * handle it as per requirment in parent component
   */
  applyNotSetToFilter?: boolean;
  dataTestId?: string;
  inputTabIndex?: number;
  variant?: 'filled' | 'outlined' | 'standard';
  useDefaultLabel?: boolean;
  label?: string;
  useDefaultPlaceholder?: boolean;
  placeholder?: string;
  selectionType?: 'single' | 'multiple';
  onApplyFilters?: (columnField: string) => void;
  disablePortal?: boolean;
}

const FieldPopoverGridFilter: React.FC<FieldPopoverGridFilterProps> = (
  props: FieldPopoverGridFilterProps
) => {
  const {
    apiRef,
    columnField,
    type,
    dataArray = [],
    defaultArray,
    valueGetterObj,
    hideFooter = false,
    inputClass,
    inputType = 'search',
    caretIconContainerClass,
    /**
     * By Default Not set option is not required to show everywhere so its excluded here
     * If required to show Not set for any case then from parent component, configure this excludeConditions option as per requirement.
     * If pass [] then Not set will be visible
     */
    excludeDateFilterConditions = ['Not set'],
    applyNotSetToFilter = false,
    dataTestId,
    inputTabIndex,
    variant = 'filled',
    useDefaultLabel = false,
    label,
    useDefaultPlaceholder = true,
    placeholder,
    selectionType = 'multiple',
    onApplyFilters,
    disablePortal = false,
  } = props;

  const iconButtonRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIdArray, setSelectedIdArray] = useState<(string | number)[]>(
    []
  );
  const [selectedRange, setSelectedRange] = useState<SelectedDateType[]>([]);
  const [selectedDateCondition, setSelectedDateCondition] = useState<
    Condition | undefined
  >();
  const [dateOperatorArray, setDateOperatorArray] = useState<string[]>([]);
  const [tempSelectedIdArray, setTempSelectedIdArray] = useState<
    (string | number)[]
  >([]);

  const filterApplied = useRef<boolean>(false);

  const filterColumns: GridColDef<FieldFilterGridData[][number]>[] = useMemo(
    () => [
      {
        field: 'label',
        headerName: t('common.selectAll'),
        width: 220,
        sortable: false,
        resizable: false,
        filterable: false,
        valueGetter: (value) => {
          return !isEmpty(valueGetterObj)
            ? (valueGetterObj?.[value] ?? '')
            : value;
        },
        cellClassName: (params) => {
          switch (params.row.value) {
            case EvidenceStatuses.PROCESSED:
              return 'processed-status';
            case EvidenceStatuses.IN_PROCESSING:
            case EvidenceStatuses.IN_PROCESSING_MANUAL:
              return 'processing-status';
            case EvidenceStatuses.PROCESSING_FAILED:
              return 'failed-status';
            default:
              return '';
          }
        },
      },
    ],
    [valueGetterObj]
  );

  useEffect(() => {
    if (!defaultArray) return;
    const selectedIds = [];
    for (const id of defaultArray) {
        const item = dataArray.find(i => i.id === id);
        if (!item) { continue; }
        const index = dataArray.indexOf(item);
        selectedIds.push(index + 1);
    }

    if (selectedIds.length > 0) { filterApplied.current = true; }

    setSelectedIdArray(selectedIds);
    setTempSelectedIdArray(selectedIds);
  }, [defaultArray]);

  const handleFilterClick = useCallback(() => {
    setTempSelectedIdArray(selectedIdArray);
    setAnchorEl(iconButtonRef.current);
  }, [selectedIdArray]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setTempSelectedIdArray(selectedIdArray);
  }, [selectedIdArray]);

  useEffect(() => {
    const filterModel: GridFilterModel =
      apiRef.current.state.filter.filterModel;

    if (!filterModel.items.some((item) => item.field === columnField)) {
      switch (type) {
        case 'options': {
          setSelectedIdArray([]);
          break;
        }
        case 'date': {
          setSelectedDateCondition(undefined);
          setSelectedRange([]);
          setDateOperatorArray([]);
          break;
        }
      }
    }
  }, [
    apiRef,
    columnField,
    type,
    JSON.stringify(apiRef.current?.state?.filter?.filterModel?.items),
  ]);

  const handleApplyFilters = useCallback(() => {
    const filterModel: GridFilterModel =
      apiRef.current.state.filter.filterModel;
    onApplyFilters?.(columnField);
    if (type === 'options') {
      const filteredData: string[] = dataArray
        .filter((item: FieldFilterGridData) =>
          tempSelectedIdArray.includes(item.id)
        )
        .map((item: FieldFilterGridData) =>
          item.value ? item.value : item.label
        );

      const updatedFilterModel: GridFilterModel = {
        items: [
          ...(tempSelectedIdArray.length > 0
            ? [
                {
                  field: columnField,
                  operator: 'isAnyOf',
                  value: filteredData,
                },
              ]
            : []),
          ...filterModel.items.filter((item) => item.field !== columnField),
        ],
      };

      apiRef.current?.setFilterModel(updatedFilterModel, 'upsertFilterItems');
      filterApplied.current = true;
      setSelectedIdArray(tempSelectedIdArray);
      handleClose();
    }
  }, [apiRef, type, dataArray, tempSelectedIdArray, columnField, handleClose]);

  useEffect(() => {
    const filterModel: GridFilterModel =
      apiRef.current.state.filter.filterModel;

    if (type === 'date') {
      const selectedDateArray = selectedRange?.filter((date) => !isEmpty(date));

      let filterModelArray: GridFilterItem[] = [];
      if (
        dateOperatorArray?.[0] === 'onOrAfter' &&
        dateOperatorArray?.[1] === 'onOrBefore'
      ) {
        const startDate = selectedDateArray?.[0]?.toDate();
        let endDate = selectedDateArray?.[1]?.toDate();
        endDate = endDate as Date;
        if (startDate) {
          filterModelArray = [
            {
              field: columnField,
              operator: dateOperatorArray?.[0],
              value: startDate,
            },
          ];
        }

        if (endDate) {
          filterModelArray = [
            ...filterModelArray,
            {
              field: columnField,
              operator: dateOperatorArray?.[1],
              value: endDate,
            },
          ];
        }
        /**
         * Add NotSet operator when its applied in GridPro and handle its
         * value in parent component to parse as per requirement in ODataQuery
         */
      } else if (selectedDateCondition === 'Not set' && applyNotSetToFilter) {
        filterModelArray = [
          {
            field: columnField,
            operator: 'NotSet',
          },
        ];
      } else {
        let date = selectedDateArray?.[0]?.toDate();
        date = date as Date;

        filterModelArray = dateOperatorArray.map((item) => {
          return {
            field: columnField,
            operator: item,
            value: date,
          };
        });
      }

      const updatedFilterModel: GridFilterModel = {
        items: [
          ...filterModelArray,
          ...filterModel.items.filter((item) => item.field !== columnField),
        ],
      };

      apiRef.current.setFilterModel(updatedFilterModel, 'upsertFilterItems');
    }
  }, [
    apiRef,
    columnField,
    selectedIdArray,
    selectedRange,
    dateOperatorArray,
    type,
  ]);

  const onSelectionChange = useCallback((ids: (number | string)[]) => {
    setTempSelectedIdArray(ids);
    setHasSelectionChanged(true);
  }, []);

  const [hasSelectionChanged, setHasSelectionChanged] = useState(false);

  useEffect(() => {
    if (selectionType === 'single' && hasSelectionChanged) {
      handleApplyFilters();
      setHasSelectionChanged(false);
    }
  }, [hasSelectionChanged, selectionType]);

  const onClickResetTextButton = useCallback(() => {
    const filterModel: GridFilterModel =
      apiRef.current.state.filter.filterModel;

    const updatedFilterModel: GridFilterModel = {
      items: [
        ...filterModel.items.filter((item) => item.field !== columnField),
      ],
    };
    apiRef.current.setFilterModel(updatedFilterModel, 'upsertFilterItems');
    setSelectedIdArray([]);
    setTempSelectedIdArray([]);
  }, [apiRef, columnField]);

  const onClickResetDateButton = useCallback(
    (dateCondition?: Condition) => {
      const filterModel: GridFilterModel =
        apiRef.current.state.filter.filterModel;

      const updatedFilterModel: GridFilterModel = {
        items: [
          ...filterModel.items.filter((item) => item.field !== columnField),
        ],
      };

      apiRef.current.setFilterModel(updatedFilterModel, 'upsertFilterItems');
      setSelectedDateCondition(dateCondition);
      setSelectedRange([]);
      setDateOperatorArray([]);
    },
    [apiRef, columnField]
  );

  const onSelectDateEvent = useCallback(
    (
      dates: SelectedDateType[],
      condition: string[],
      dateCondition?: Condition
    ) => {
      setSelectedRange(dates);
      setDateOperatorArray(condition);
      setSelectedDateCondition(dateCondition);
      onApplyFilters?.(columnField);
    },
    []
  );

  const clearFilter = () => {
    onClickResetTextButton();
    setTempSelectedIdArray([]);
    setAnchorEl(null);
  };

  const inputValue = useMemo(() => {
    if (!isEmpty(selectedIdArray ?? []) && filterApplied.current) {
      const count = selectedIdArray.length;
      return t('fieldManagement.fieldTable.filter') + ' (' + count + ')';
    }

    return '';
  }, [selectedIdArray]);

  if (type === 'date') {
    return (
      <div
        role="none"
        tabIndex={-1}
        onBlur={(e) => {
          /**
           * Reason to wrapped DateFilter under div with onBlur because when FieldPopoverDateFilter used with
           * DataGrid for header filter then its create issue for DateField component under FieldPopoverDateFilter,
           * When first time user click on DataField for input date then focus get lost due to first time key press event so
           * user again need to click on DateField and input date, to resolved this issue, we need to wrape it under
           * div and onBlur eent, reset focus of header and filter using DataGrid Ref,
           * This focus lost event occure because DateField component internally try to convert uncontrolled to controlled component and
           * this get component rerender without any change
           */
          // Only clear focus if blur is going outside the component
          const nextFocus = e.relatedTarget as HTMLElement | null;
          const isStillInsideGrid =
            nextFocus?.closest('[role="grid"]') !== null;

          const isDatePickerPopover =
            nextFocus?.closest('.MuiPopover-root') !== null ||
            nextFocus?.closest('.MuiPickersPopper-root') !== null;

          if (!isStillInsideGrid && !isDatePickerPopover) {
            apiRef.current.setColumnHeaderFilterFocus('');
            apiRef.current.setColumnHeaderFocus('');
          }
        }}
      >
        <FieldPopoverDateFilter
          type={'query-date-picker'}
          placeholder={''}
          advanceLink={false}
          selectedDateCondition={selectedDateCondition}
          selectedDate={selectedRange}
          onApplyClick={onSelectDateEvent}
          onClearClick={onClickResetDateButton}
          inputClass={inputClass}
          inputType={inputType}
          caretIconContainerClass={caretIconContainerClass}
          excludeConditions={excludeDateFilterConditions}
          applyNotSetToFilter={applyNotSetToFilter}
          dataTestId={dataTestId}
          columnField={columnField}
          inputTabIndex={inputTabIndex}
          disablePortal={disablePortal}
        />
      </div>
    );
  }

  return (
    <>
      <Box className={'aur-field-popover-grid-filter'} ref={iconButtonRef}>
        <AurTextField
          className={`aur-search-input ${inputClass ?? ''}`}
          autoComplete={'off'}
          type={inputType}
          onKeyDown={(e) => {
            if (e.code === 'Enter') {
              handleFilterClick();
            }
          }}
          onClick={handleFilterClick}
          variant={variant}
          size={'small'}
          placeholder={
            useDefaultPlaceholder
              ? t('fieldManagement.fieldTable.filter')
              : placeholder
          }
          label={
            useDefaultLabel ? t('fieldManagement.fieldTable.filter') : label
          }
          value={inputValue}
          ariaLabel={
            useDefaultLabel ? t('fieldManagement.fieldTable.filter') : label
          }
          onChange={(_) => {
            if (!isEmpty(selectedIdArray ?? [])) {
              clearFilter();
            }
          }}
          InputProps={{
            // disableUnderline: true,
            endAdornment: !isEmpty(selectedIdArray ?? []) &&
              inputType === 'text' && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      clearFilter();
                    }}
                    edge="end"
                    tabIndex={0}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            tabIndex: inputTabIndex ?? 0,
          }}
          data-testid={dataTestId}
        />
        <Box
          className={`aur-caret-icon-container ${caretIconContainerClass ?? ''}`}
        >
          <IconButton
            aria-label={`dropdown-${columnField}`}
            data-testid={`dropdown-${columnField}`}
            className={'aur-caret-icon-button'}
            onClick={handleFilterClick}
            tabIndex={0}
          >
            <AurIcon
              icon={anchorEl ? IconNames.CaretUp : IconNames.CaretDown}
            />
          </IconButton>
        </Box>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        disableScrollLock={false}
        className="filter-modal-popover"
      >
        <div role="region">
        <AurDataGrid
          key={'filter-table'}
          rows={dataArray}
          columns={filterColumns}
          showPagination={false}
          selectedIds={tempSelectedIdArray}
          currentPage={0}
          onSelectionChange={onSelectionChange}
          itemsPerPage={dataArray.length}
          checkboxSelection={selectionType === 'single' ? false : true}
          columnHeaderHeight={selectionType === 'single' ? 0 : undefined}
          hideFooter={hideFooter || selectionType === 'single' ? true : false}
          footerChildComponent={
            <Box className={'aur-filter-button-container'}>
              <AurButton
                onClick={onClickResetTextButton}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onClickResetTextButton();
                  }
                }}
                size={'Small'}
                label={t('common.clearAll')}
                type={'text'}
                ariaLabel={t('common.clearAll')}
              />
              <AurButton
                onClick={handleApplyFilters}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleApplyFilters();
                    handleClose();
                  }
                }}
                size={'Small'}
                label={t('buttons.apply')}
                ariaLabel={t('buttons.apply')}
                type={'continue'}
                dataTestId="apply-grid-filter-button"
              />
            </Box>
          }
        />
        </div>
      </Popover>
    </>
  );
};

export default FieldPopoverGridFilter;
